/**
 * Flag an account for admin review.
 *
 * Escape valve for account-level issues that don't map to any of the
 * five structured visit outcomes — store permanently closed, buyer
 * change, distribution misroute, account data needs correction.
 * Submits a free-form reason to POST /api/field/accounts/{id}/flag;
 * the backend writes an entry to the auth audit log for admin
 * review via existing tooling.
 *
 * Deliberately NOT a sixth outcome.  Outcomes encode the
 * conversation's state and drive the cooldown that keeps a rep from
 * re-visiting the same account too soon; flags are about the
 * account record itself and need admin judgment, not a cooldown.
 */

import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  type AccountFlagHistoryItem,
  fetchAccountFlags,
  flagAccountForReview,
} from '../../api/field';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';
const FLAG_AMBER = '#a86b1e';
const REASON_MAX = 2_000;

interface FlagAccountDialogProps {
  open: boolean;
  accountId: number;
  accountName: string;
  onClose: () => void;
}

export function FlagAccountDialog({
  open,
  accountId,
  accountName,
  onClose,
}: FlagAccountDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Prior flags on this account — read straight off the audit log
  // via /api/field/accounts/{id}/flags.  Surfaced above the form so
  // the rep can see whether the account has been flagged before
  // (avoid duplicates) and what reasons came up.
  const flagsQuery = useQuery({
    queryKey: ['field', 'account', accountId, 'flags'],
    queryFn: () => fetchAccountFlags(accountId),
    enabled: open,
    staleTime: 30_000,
  });
  const priorFlags = flagsQuery.data?.items ?? [];

  const mutation = useMutation({
    mutationFn: () => flagAccountForReview(accountId, { reason: reason.trim() }),
    onSuccess: () => {
      // Brief confirmation state, then auto-close.  Invalidate the
      // flags query so the timeline picks up the new entry next time
      // the dialog opens on this account.
      queryClient.invalidateQueries({ queryKey: ['field', 'account', accountId, 'flags'] });
      // Flagging an account is logged work, so it counts toward
      // the momentum window too.
      queryClient.invalidateQueries({ queryKey: ['field', 'momentum'] });
      setSubmitted(true);
      setTimeout(() => {
        handleClose();
      }, 1400);
    },
  });

  const handleClose = () => {
    setReason('');
    setSubmitted(false);
    mutation.reset();
    onClose();
  };

  const valid = reason.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        paper: {
          sx: {
            bgcolor: '#fdf9f0',
            borderRadius: { xs: 0, sm: 2 },
            border: { xs: 'none', sm: '1px solid rgba(187, 140, 63, 0.22)' },
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          fontSize: 17,
          color: INK,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <OutlinedFlagIcon sx={{ color: FLAG_AMBER, fontSize: 20 }} />
        Flag for admin review
        <Box sx={{ flexGrow: 1 }} />
        <Typography
          component="span"
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 12.5,
            fontWeight: 500,
            color: 'rgba(15, 10, 7, 0.55)',
          }}
        >
          {accountName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: 'rgba(187, 140, 63, 0.22)' }}>
        {submitted ? (
          <Alert
            severity="success"
            sx={{
              fontFamily: FONT_INTER,
              bgcolor: 'rgba(34, 134, 90, 0.12)',
              border: '1px solid rgba(34, 134, 90, 0.32)',
              color: '#22865a',
            }}
          >
            Flag submitted. An admin will review this account.
          </Alert>
        ) : (
          <>
            <PreviousFlags flags={priorFlags} loading={flagsQuery.isLoading} />
            {/* Editorial preamble — separates flags from visits and
                names the concrete scenarios where reps should reach
                for this affordance.  Steers reps away from using a
                flag in place of a missing outcome (which we
                deliberately avoided). */}
            <Box
              sx={{
                mb: 2,
                px: 1.5,
                py: 1.25,
                borderRadius: 1.25,
                bgcolor: `${FLAG_AMBER}0c`,
                border: `1px solid ${FLAG_AMBER}40`,
              }}
            >
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: FLAG_AMBER,
                  mb: 0.5,
                }}
              >
                When to use
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 12.5,
                  color: 'rgba(15, 10, 7, 0.72)',
                  lineHeight: 1.55,
                }}
              >
                Use this for <strong>account-level</strong> issues that need admin attention — store
                permanently closed, ownership change, distribution misroute, address / buyer record
                needs correcting.{' '}
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  fontFamily: FONT_INTER,
                  fontSize: 11.5,
                  color: 'rgba(15, 10, 7, 0.55)',
                  lineHeight: 1.5,
                }}
              >
                For a visit that just doesn’t fit a clean outcome, use <strong>Log Visit</strong>{' '}
                with the closest outcome and explain in the note — that keeps the timeline complete.
              </Typography>
            </Box>

            {mutation.error && (
              <Alert severity="error" sx={{ fontFamily: FONT_INTER, mb: 1.5 }}>
                {(mutation.error as Error).message}
              </Alert>
            )}

            <TextField
              label="Reason for flag"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              placeholder="e.g. Store has been closed for 6 months — saw the boarded windows. Account record should be marked inactive."
              inputProps={{ maxLength: REASON_MAX }}
              helperText={`${reason.length.toLocaleString()} / ${REASON_MAX.toLocaleString()}`}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={mutation.isPending}>
          {submitted ? 'Close' : 'Cancel'}
        </Button>
        {!submitted && (
          <Button
            variant="contained"
            disabled={!valid || mutation.isPending}
            onClick={() => mutation.mutate()}
            sx={{
              background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
              color: INK,
              fontFamily: FONT_INTER,
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              fontSize: 12,
              px: 2.25,
              boxShadow: 'none',
              '&:hover': {
                background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
                boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
              },
            }}
          >
            {mutation.isPending ? 'Submitting…' : 'Submit Flag'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// =====================================================================
// PreviousFlags — chronological history of admin-review flags for
// this account.  Mirrors the visit-notes "Previous touchpoints"
// pattern visually so the two surfaces feel like one editorial
// family.  Three states:
//
//   * loading — small gold spinner
//   * empty   — dashed amber callout reassuring the rep this is the
//               first flag (and surfacing the value of doing so well)
//   * has-data — count chip + scrollable list, newest first.
//
// Each row shows: amber flag rail · date · rep name · reason text.
// =====================================================================
function PreviousFlags({ flags, loading }: { flags: AccountFlagHistoryItem[]; loading: boolean }) {
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 1.5,
          mb: 2,
        }}
      >
        <CircularProgress size={18} sx={{ color: GOLD }} />
      </Box>
    );
  }

  if (flags.length === 0) {
    return (
      <Box
        sx={{
          mb: 2,
          px: 1.5,
          py: 1.25,
          borderRadius: 1.25,
          bgcolor: 'rgba(187, 140, 63, 0.06)',
          border: '1px dashed rgba(187, 140, 63, 0.32)',
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: GOLD,
            mb: 0.5,
          }}
        >
          No prior flags
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 12.5,
            color: 'rgba(15, 10, 7, 0.62)',
            lineHeight: 1.5,
          }}
        >
          This will be the first flag on the account. Give admin a clear, specific reason — they
          rely on your description to decide what action to take.
        </Typography>
      </Box>
    );
  }

  // Defensive newest-first sort regardless of API ordering.
  const sorted = [...flags].sort((a, b) =>
    a.flagged_at < b.flagged_at ? 1 : a.flagged_at > b.flagged_at ? -1 : 0,
  );

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: 1.25,
        bgcolor: `${FLAG_AMBER}0a`,
        border: `1px solid ${FLAG_AMBER}40`,
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: `1px solid ${FLAG_AMBER}22`,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: FLAG_AMBER,
          }}
        >
          Prior flags
        </Typography>
        <Chip
          label={sorted.length}
          size="small"
          sx={{
            height: 18,
            fontSize: 10.5,
            fontFamily: FONT_INTER,
            fontWeight: 700,
            bgcolor: `${FLAG_AMBER}18`,
            color: FLAG_AMBER,
            border: `1px solid ${FLAG_AMBER}55`,
            '& .MuiChip-label': { px: 0.85 },
          }}
        />
      </Box>
      <Box
        sx={{
          maxHeight: 220,
          overflowY: 'auto',
          px: 1.5,
          py: 1,
          scrollbarColor: 'rgba(168, 107, 30, 0.42) transparent',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 9 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(168, 107, 30, 0.38)',
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(168, 107, 30, 0.6)',
            backgroundClip: 'content-box',
          },
        }}
      >
        <Stack spacing={1.25}>
          {sorted.map((flag) => (
            <PriorFlagRow key={flag.id} flag={flag} />
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

function PriorFlagRow({ flag }: { flag: AccountFlagHistoryItem }) {
  // Parse ISO timestamp once.  Show both the calendar date and the
  // relative time ("2 weeks ago") so the rep can scan recency at a
  // glance.
  const flaggedDate = new Date(flag.flagged_at);
  const dateLabel = !isNaN(flaggedDate.getTime())
    ? flaggedDate.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : flag.flagged_at;
  const relative = !isNaN(flaggedDate.getTime()) ? relativeFromNow(flaggedDate) : null;

  return (
    <Box
      sx={{
        pl: 1.25,
        pr: 0.5,
        py: 0.75,
        borderLeft: `3px solid ${FLAG_AMBER}`,
        bgcolor: '#fff',
        borderRadius: '0 4px 4px 0',
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        flexWrap="wrap"
        rowGap={0.3}
        sx={{ mb: 0.4 }}
      >
        <Typography sx={{ fontFamily: FONT_INTER, fontSize: 12, fontWeight: 700, color: INK }}>
          {dateLabel}
        </Typography>
        {relative && (
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 11,
              color: 'rgba(15, 10, 7, 0.45)',
            }}
          >
            · {relative}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 11,
            fontStyle: 'italic',
            color: 'rgba(15, 10, 7, 0.5)',
          }}
        >
          {flag.rep_name || 'Unknown rep'}
        </Typography>
      </Stack>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 12.5,
          color: 'rgba(15, 10, 7, 0.78)',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
        }}
      >
        {flag.reason}
      </Typography>
    </Box>
  );
}

// "2 weeks ago" / "3 days ago" / "today" — keeps the recency
// human-readable next to the absolute date.
function relativeFromNow(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const days = Math.floor(diffMs / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}
